import React, { useState } from 'react';
import { styled, createTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { DynamicValue } from '@fulcrumapp/fulcrum-core';
import ChecklistEditor from './checklist-editor';
import LocalStoragePreferences from '../../utils/local-storage-preferences';
import Button from '../../components/button';
import CustomDrawer from '../../shared/material_components/custom-drawer';

interface Props {
  open: boolean;
  onClose: () => void;
  value: DynamicValue;
  onChange: (value: DynamicValue) => void;
  isCreating: boolean;
}

const theme = createTheme();
const CloseButton = styled(IconButton)({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
});

const EditChecklistDialog = ({
  open,
  onClose,
  value,
  onChange,
  isCreating,
}: Props) => {
  const originalChecklistValue = new DynamicValue(
    value.element,
    value.items[0] ? [value.items[0].toJSON()] : [],
  );
  const [checklistValue, setChecklistValue] = useState(originalChecklistValue);

  const handleAddToTask = () => {
    onChange(checklistValue);
    onClose();
  };

  const disableAddToTask = checklistValue.items.length === 0
    || checklistValue.items[0].elements.length === 0;

  const localStorage = new LocalStoragePreferences();
  const useDrawer = localStorage.useDrawerInEditor();
  const title = isCreating ? 'Add Checklist' : 'Edit Checklist';
  const positive = isCreating ? 'Add to task' : 'Save Changes';

  return useDrawer ? (
    <CustomDrawer
      title={title}
      open={open}
      onNegative={onClose}
      onPositive={handleAddToTask}
      positive={positive}
    >
      <ChecklistEditor
        value={originalChecklistValue}
        onChange={setChecklistValue}
      />
    </CustomDrawer>
  ) : (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {isCreating ? 'Add Checklist' : 'Edit Checklist'}
        <CloseButton
          aria-label="close"
          onClick={onClose}
          size="large"
        >
          <CloseIcon />
        </CloseButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          width: '100%',
          padding: theme.spacing(2),
        }}
      >
        <ChecklistEditor
          value={originalChecklistValue}
          onChange={setChecklistValue}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleAddToTask}
          disabled={isCreating && disableAddToTask}
          color="secondary"
        >
          {isCreating ? 'Add to task' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditChecklistDialog;
